import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarDays, Settings, ChevronLeft, ChevronRight, Clock, Video, Phone, Loader2, CheckCircle2, AlertCircle, RefreshCw, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
type ConnectionState = 'disconnected' | 'connecting' | 'success' | 'error' | 'connected';
type CalendarView = 'month' | 'day';
const EVENTS = [{
  id: 1,
  title: 'Product Sync',
  time: '10:00 AM – 11:00 AM',
  type: 'video' as const,
  location: 'Google Meet',
  attendees: ['Alex', 'Sarah', 'Mike'],
  aiManaged: false,
  accent: 'bg-blue-500'
}, {
  id: 2,
  title: 'Client Discovery Call',
  time: '1:30 PM – 2:15 PM',
  type: 'phone' as const,
  location: '+1 (555) 012-3456',
  attendees: ['Alex', 'Client'],
  aiManaged: true,
  accent: 'bg-vexu'
}, {
  id: 3,
  title: 'Design Review',
  time: '3:00 PM – 4:00 PM',
  type: 'video' as const,
  location: 'Zoom',
  attendees: ['Design Team'],
  aiManaged: false,
  accent: 'bg-purple-500'
}, {
  id: 4,
  title: 'Investor Update',
  time: '5:00 PM – 5:30 PM',
  type: 'video' as const,
  location: 'Google Meet',
  attendees: ['Alex', 'Board'],
  aiManaged: true,
  accent: 'bg-vexu'
}];
const EVENT_DAYS = [3, 7, 12, 14, 18, 24, 26];
const fadeSlide = {
  initial: {
    opacity: 0,
    y: 16
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: -16
  },
  transition: {
    duration: 0.3,
    ease: [0.4, 0, 0.2, 1]
  }
};
export function CalendarScreen() {
  const {
    isLight
  } = useTheme();
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [provider, setProvider] = useState<'google' | 'outlook' | null>(null);
  const [view, setView] = useState<CalendarView>('day');
  const [hasCalendarData, setHasCalendarData] = useState(false);
  const handleConnect = (p: 'google' | 'outlook') => {
    setProvider(p);
    setConnectionState('connecting');
    setTimeout(() => {
      setConnectionState('success');
      setTimeout(() => setConnectionState('connected'), 1400);
    }, 2200);
  };
  const handleRetry = () => {
    if (provider) handleConnect(provider);
  };
  // ─── Disconnected ────────────────────────────────────────
  const renderDisconnected = () => <motion.div key="disconnected" {...fadeSlide} className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-id="element-513">
      <div className="relative mb-10" data-id="element-514">
        <div className={`absolute inset-0 rounded-full blur-3xl scale-150 animate-pulse pointer-events-none ${isLight ? 'bg-vexu/20' : 'bg-vexu/15'}`} data-id="element-515" />
        <div className={`relative w-24 h-24 rounded-[28px] border flex items-center justify-center transition-colors duration-500 ${isLight ? 'bg-white border-slate-200 shadow-xl shadow-vexu/10' : 'bg-dark-card border-white/10 shadow-2xl shadow-black/60'}`} data-id="element-516">
          <CalendarDays className={`w-11 h-11 ${isLight ? 'text-vexu-600' : 'text-white'}`} strokeWidth={1.6} data-id="element-517" />
        </div>
      </div>

      <h2 className={`text-[22px] font-bold mb-2 tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-518">
        Connect Your Calendar
      </h2>
      <p className={`text-sm leading-relaxed mb-10 max-w-[260px] ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-519">
        Let Vexu.ai manage your schedule, book meetings from calls, and keep you
        organized.
      </p>

      <div className="w-full space-y-3 max-w-xs" data-id="element-520">
        <button onClick={() => handleConnect('google')} className={`w-full h-[52px] rounded-2xl backdrop-blur-lg border flex items-center justify-center gap-3 font-medium active:scale-[0.98] transition-all ${isLight ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.04] border-white/[0.08] text-white hover:bg-white/[0.08]'}`} data-id="element-521">
          <svg className="w-5 h-5" viewBox="0 0 24 24" data-id="element-522">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-523" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-524" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-525" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-526" />
          </svg>
          Continue with Google
        </button>

        <button onClick={() => handleConnect('outlook')} className={`w-full h-[52px] rounded-2xl backdrop-blur-lg border flex items-center justify-center gap-3 font-medium active:scale-[0.98] transition-all ${isLight ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.04] border-white/[0.08] text-white hover:bg-white/[0.08]'}`} data-id="element-527">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" data-id="element-528">
            <path d="M11.5 24L0 20.5V3.5L11.5 0V24Z" fill="#0078D4" data-id="element-529" />
            <path d="M24 5.5V18.5C24 19.33 23.33 20 22.5 20H13V4H22.5C23.33 4 24 4.67 24 5.5Z" fill="#0078D4" opacity="0.7" data-id="element-530" />
            <ellipse cx="6" cy="12" rx="3.5" ry="4.5" fill="white" data-id="element-531" />
          </svg>
          Continue with Outlook
        </button>
      </div>
    </motion.div>;
  // ─── Connecting ──────────────────────────────────────────
  const renderConnecting = () => <motion.div key="connecting" {...fadeSlide} className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-id="element-532">
      <div className="relative z-10 flex flex-col items-center text-center w-full max-w-[280px]" data-id="element-533">
        <div className="relative w-32 h-32 mb-8" data-id="element-534">
          <motion.div animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.1, 0.3]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-full border-2 border-vexu/30" data-id="element-535" />
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute inset-4 rounded-full border-2 border-transparent border-t-vexu border-r-vexu/30" data-id="element-536" />
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-537">
            <motion.div animate={{
            scale: [1, 1.1, 1]
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} data-id="element-538">
              <CalendarDays className="w-10 h-10 text-vexu" data-id="element-539" />
            </motion.div>
          </div>
        </div>
        <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-540">
          Syncing Calendar
        </p>
        <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-541">
          Securely importing your events...
        </p>
      </div>
    </motion.div>;
  // ─── Success ─────────────────────────────────────────────
  const renderSuccess = () => <motion.div key="success" {...fadeSlide} className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-id="element-542">
      <motion.div initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 20
    }} className={`w-20 h-20 rounded-full border flex items-center justify-center mb-6 transition-colors ${isLight ? 'bg-vexu/10 border-vexu/30 shadow-[0_0_30px_rgba(0,200,83,0.2)]' : 'bg-vexu/15 border-vexu/25 shadow-[0_0_40px_rgba(0,200,83,0.15)]'}`} data-id="element-543">
        <CheckCircle2 className={`w-10 h-10 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-544" />
      </motion.div>
      <h3 className={`text-xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-545">
        Connected
      </h3>
      <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-546">
        Your calendar is synced with Vexu.ai
      </p>
    </motion.div>;
  // ─── Error ───────────────────────────────────────────────
  const renderError = () => <motion.div key="error" {...fadeSlide} className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-id="element-547">
      <div className={`w-20 h-20 rounded-full border flex items-center justify-center mb-6 transition-colors ${isLight ? 'bg-red-50 border-red-200' : 'bg-red-500/10 border-red-500/20'}`} data-id="element-548">
        <AlertCircle className={`w-10 h-10 ${isLight ? 'text-red-500' : 'text-red-400'}`} data-id="element-549" />
      </div>
      <h3 className={`text-xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-550">
        Connection Failed
      </h3>
      <p className={`text-sm mb-6 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-551">
        We couldn't connect to your calendar. Please try again.
      </p>
      <Button variant="secondary" onClick={handleRetry} icon={<RefreshCw className="w-4 h-4" data-id="element-553" />} data-id="element-552">
        Retry Connection
      </Button>
    </motion.div>;
  // ─── Connected: Calendar ─────────────────────────────────
  const renderConnected = () => <motion.div key="connected" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="flex-1 flex flex-col h-full" data-id="element-554">
      {/* Header */}
      <div className="px-6 pt-6 pb-2" data-id="element-555">
        <div className="flex justify-between items-center mb-5" data-id="element-556">
          <h1 className={`text-2xl font-bold tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-557">
            Calendar
          </h1>
          <button onClick={() => setConnectionState('disconnected')} className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.04] border-white/[0.08] hover:bg-white/[0.08]'}`} data-id="element-558">
            <Settings className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-559" />
          </button>
        </div>

        {/* View toggle */}
        <div className={`flex p-1 border rounded-xl mb-5 transition-colors ${isLight ? 'bg-slate-200/50 border-slate-200' : 'bg-white/[0.04] border-white/[0.06]'}`} data-id="element-560">
          {(['day', 'month'] as const).map(v => <button key={v} onClick={() => setView(v)} className={`relative flex-1 py-1.5 text-sm font-medium rounded-lg capitalize transition-all ${view === v ? isLight ? 'text-slate-900 shadow-sm' : 'text-white' : isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-561">
              {view === v && <motion.div layoutId="calViewToggle" className={`absolute inset-0 rounded-lg border ${isLight ? 'bg-white border-slate-200' : 'bg-white/[0.08] border-white/[0.06]'}`} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 35
          }} data-id="element-562" />}
              <span className="relative z-10" data-id="element-563">{v}</span>
            </button>)}
        </div>

        {/* Date nav */}
        <div className="flex items-center justify-between mb-4" data-id="element-564">
          <h2 className={`text-base font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-565">
            {view === 'day' ? 'Today, Feb 24' : 'February 2026'}
          </h2>
          <div className="flex gap-1.5" data-id="element-566">
            <button className={`p-1.5 rounded-lg border transition-colors ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.04] border-white/[0.06] hover:bg-white/[0.08]'}`} data-id="element-567">
              <ChevronLeft className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-568" />
            </button>
            <button className={`p-1.5 rounded-lg border transition-colors ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.04] border-white/[0.06] hover:bg-white/[0.08]'}`} data-id="element-569">
              <ChevronRight className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-570" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-28" data-id="element-571">
        <AnimatePresence mode="wait" data-id="element-572">
          {!hasCalendarData ? <motion.div key="empty" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.3
        }} data-id="element-573">
              {view === 'day' /* Empty Day View */ ? <div className="flex flex-col items-center justify-center text-center pt-16" data-id="element-574">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-575">
                    <CalendarDays className={`w-7 h-7 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-576" />
                  </div>
                  <p className={`text-sm font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-577">
                    No events today
                  </p>
                  <p className={`text-xs leading-relaxed max-w-[220px] mb-5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-578">
                    Your calendar is synced but there are no events scheduled
                    for today.
                  </p>
                  <button onClick={() => setHasCalendarData(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-vexu text-white text-sm font-medium hover:bg-vexu-600 transition-colors" data-id="element-579">
                    <Zap className="w-4 h-4" data-id="element-580" />
                    Simulate Events
                  </button>
                </div> /* Empty Month View — show grid but no event dots */ : <div data-id="element-581">
                  <div className={`backdrop-blur-xl border rounded-2xl p-4 ${isLight ? 'bg-white/80 border-slate-200/60 shadow-sm' : 'bg-white/[0.03] border-white/[0.07]'}`} data-id="element-582">
                    <div className="grid grid-cols-7 gap-0.5 mb-1" data-id="element-583">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i} className={`text-center text-[11px] font-bold py-2 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-584">
                          {d}
                        </div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-0.5" data-id="element-585">
                      {Array.from({
                  length: 35
                }).map((_, i) => {
                  const day = i + 1;
                  const inMonth = day >= 1 && day <= 28;
                  const isToday = day === 24;
                  return <div key={i} className={`
                              aspect-square flex flex-col items-center justify-center rounded-xl relative cursor-pointer transition-all
                              ${isToday ? isLight ? 'bg-vexu/10 border border-vexu/20 shadow-sm' : 'bg-vexu/15 border border-vexu/25 shadow-[0_0_12px_rgba(0,200,83,0.1)]' : isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.04]'}
                              ${!inMonth ? 'opacity-20' : ''}
                            `} data-id="element-586">
                            <span className={`text-[13px] ${isToday ? isLight ? 'text-vexu-700 font-bold' : 'text-vexu font-bold' : isLight ? 'text-slate-700 font-medium' : 'text-zinc-300 font-medium'}`} data-id="element-587">
                              {inMonth ? day : ''}
                            </span>
                          </div>;
                })}
                    </div>
                  </div>

                  <div className="flex flex-col items-center justify-center text-center pt-8" data-id="element-588">
                    <p className={`text-sm font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-589">
                      No events this month
                    </p>
                    <p className={`text-xs leading-relaxed max-w-[220px] mb-5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-590">
                      Events booked by Vexu.ai or from your calendar will appear
                      here.
                    </p>
                    <button onClick={() => setHasCalendarData(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-vexu text-white text-sm font-medium hover:bg-vexu-600 transition-colors" data-id="element-591">
                      <Zap className="w-4 h-4" data-id="element-592" />
                      Simulate Events
                    </button>
                  </div>
                </div>}
            </motion.div> : view === 'day' ? <motion.div key="dayView" initial={{
          opacity: 0,
          x: -12
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 12
        }} transition={{
          duration: 0.25
        }} className="space-y-3" data-id="element-593">
              {EVENTS.map((event, i) => <motion.div key={event.id} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.06,
            duration: 0.3
          }} className={`backdrop-blur-xl border rounded-2xl p-4 relative overflow-hidden group transition-colors ${isLight ? 'bg-white/80 border-slate-200/60 shadow-sm hover:bg-white' : 'bg-white/[0.03] border-white/[0.07] shadow-xl hover:bg-white/[0.05]'}`} data-id="element-594">
                  {event.aiManaged && <div className={`absolute top-0 right-0 text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-bl-xl border-b border-l ${isLight ? 'bg-vexu/10 text-vexu-700 border-vexu/20' : 'bg-vexu/10 text-vexu border-vexu/15'}`} data-id="element-595">
                      AI Scheduled
                    </div>}
                  <div className="flex items-start gap-3.5" data-id="element-596">
                    <div className={`w-1 h-14 rounded-full mt-0.5 ${event.accent} opacity-80`} data-id="element-597" />
                    <div className="flex-1 min-w-0" data-id="element-598">
                      <h3 className={`font-semibold text-[15px] mb-1.5 truncate pr-16 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-599">
                        {event.title}
                      </h3>
                      <div className={`flex items-center gap-1.5 text-xs mb-3 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-600">
                        <Clock className="w-3.5 h-3.5 shrink-0" data-id="element-601" />
                        <span data-id="element-602">{event.time}</span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-603">
                        <div className={`flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-lg border truncate ${isLight ? 'bg-slate-50 border-slate-200 text-slate-600' : 'bg-white/[0.04] border-white/[0.06] text-zinc-300'}`} data-id="element-604">
                          {event.type === 'video' ? <Video className="w-3 h-3 shrink-0" data-id="element-605" /> : <Phone className="w-3 h-3 shrink-0" data-id="element-606" />}
                          <span className="truncate" data-id="element-607">{event.location}</span>
                        </div>
                        <div className="flex -space-x-1.5" data-id="element-608">
                          {event.attendees.slice(0, 3).map((att, j) => <div key={j} className={`w-6 h-6 rounded-full border flex items-center justify-center text-[9px] font-bold ${isLight ? 'bg-slate-100 border-white text-slate-600' : 'bg-white/[0.06] border-dark text-zinc-400'}`} data-id="element-609">
                              {att.charAt(0)}
                            </div>)}
                          {event.attendees.length > 3 && <div className={`w-6 h-6 rounded-full border flex items-center justify-center text-[9px] font-bold ${isLight ? 'bg-slate-100 border-white text-slate-500' : 'bg-white/[0.06] border-dark text-zinc-500'}`} data-id="element-610">
                              +{event.attendees.length - 3}
                            </div>}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>)}
            </motion.div> : <motion.div key="monthView" initial={{
          opacity: 0,
          x: 12
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -12
        }} transition={{
          duration: 0.25
        }} data-id="element-611">
              <div className={`backdrop-blur-xl border rounded-2xl p-4 ${isLight ? 'bg-white/80 border-slate-200/60 shadow-sm' : 'bg-white/[0.03] border-white/[0.07]'}`} data-id="element-612">
                <div className="grid grid-cols-7 gap-0.5 mb-1" data-id="element-613">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i} className={`text-center text-[11px] font-bold py-2 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-614">
                      {d}
                    </div>)}
                </div>
                <div className="grid grid-cols-7 gap-0.5" data-id="element-615">
                  {Array.from({
                length: 35
              }).map((_, i) => {
                const day = i + 1;
                const inMonth = day >= 1 && day <= 28;
                const isToday = day === 24;
                const hasEvent = EVENT_DAYS.includes(day);
                return <div key={i} className={`
                          aspect-square flex flex-col items-center justify-center rounded-xl relative cursor-pointer transition-all
                          ${isToday ? isLight ? 'bg-vexu/10 border border-vexu/20 shadow-sm' : 'bg-vexu/15 border border-vexu/25 shadow-[0_0_12px_rgba(0,200,83,0.1)]' : isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.04]'}
                          ${!inMonth ? 'opacity-20' : ''}
                        `} data-id="element-616">
                        <span className={`text-[13px] ${isToday ? isLight ? 'text-vexu-700 font-bold' : 'text-vexu font-bold' : isLight ? 'text-slate-700 font-medium' : 'text-zinc-300 font-medium'}`} data-id="element-617">
                          {inMonth ? day : ''}
                        </span>
                        {hasEvent && inMonth && <div className={`w-1 h-1 rounded-full absolute bottom-1.5 ${isToday ? isLight ? 'bg-vexu-600' : 'bg-vexu' : isLight ? 'bg-slate-300' : 'bg-zinc-500'}`} data-id="element-618" />}
                      </div>;
              })}
                </div>
              </div>

              <div className="mt-5" data-id="element-619">
                <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-620">
                  Events on Feb 24
                </h3>
                <div className="space-y-2.5" data-id="element-621">
                  {EVENTS.slice(0, 2).map(event => <div key={event.id} className={`border rounded-xl p-3 flex items-center gap-3 ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'bg-white/[0.03] border-white/[0.07]'}`} data-id="element-622">
                      <div className={`w-1 h-10 rounded-full ${event.accent} opacity-80`} data-id="element-623" />
                      <div className="flex-1 min-w-0" data-id="element-624">
                        <h4 className={`text-sm font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-625">
                          {event.title}
                        </h4>
                        <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-626">
                          {event.time}
                        </p>
                      </div>
                      {event.aiManaged && <span className={`text-[9px] font-bold px-2 py-0.5 rounded-md border ${isLight ? 'text-vexu-700 bg-vexu/10 border-vexu/20' : 'text-vexu bg-vexu/10 border-vexu/15'}`} data-id="element-627">
                          AI
                        </span>}
                    </div>)}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
  return <div className="h-full flex flex-col relative overflow-hidden" data-id="element-628">
      {/* Background orbs */}
      <div className={`absolute top-0 right-0 w-72 h-72 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/10' : 'bg-vexu/[0.04]'}`} data-id="element-629" />
      <div className={`absolute bottom-32 -left-20 w-64 h-64 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-blue-500/10' : 'bg-blue-500/[0.03]'}`} data-id="element-630" />

      <AnimatePresence mode="wait" data-id="element-631">
        {connectionState === 'disconnected' && renderDisconnected()}
        {connectionState === 'connecting' && renderConnecting()}
        {connectionState === 'success' && renderSuccess()}
        {connectionState === 'error' && renderError()}
        {connectionState === 'connected' && renderConnected()}
      </AnimatePresence>
    </div>;
}